import React from 'react';
import { RefreshCw, TriangleAlert } from 'lucide-react';

/**
 * Catches render errors in a subtree (used per route) so one broken page
 * doesn't blank the whole app. `resetKey` (e.g. the pathname) clears the error.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ error: null });
    }
  }

  componentDidCatch(error, info) {
    // Only the error and component stack, never user data.
    console.error('Route error:', error?.message, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div role="alert" className="mx-auto flex min-h-[50vh] max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-loss-soft text-loss">
          <TriangleAlert className="h-6 w-6" aria-hidden="true" />
        </span>
        <div>
          <h1 className="text-lg font-semibold text-fg">This page hit an error</h1>
          <p className="mt-1 text-sm text-fg-muted">Your data is safe. Reload the page to try again, or pick another page from the menu.</p>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex h-10 items-center gap-2 rounded-control bg-accent px-4 text-sm font-medium text-accent-fg hover:bg-accent-hover"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Reload
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
